"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Loader2, NotebookPen, Save, X } from "lucide-react";
import { api, type NoteListItem } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * 书本侧边笔记抽屉：内容页右下角的"笔记"入口。
 * 点击弹出右侧滑入抽屉：
 *  - 当前页笔记（自动保存，可编辑）
 *  - 本章笔记汇总（同章节其它笔记，可跳转）
 * 设计参考 Notion/Obsidian 侧边笔记：不离开当前页面，随时查看与记录。
 */
export default function NoteDrawer({
  pageId,
  conceptId,
  sectionId,
  chapterId,
  title,
  buttonClassName,
  buttonOffsetRight,
}: {
  pageId: string;
  conceptId?: string;
  sectionId?: string;
  chapterId?: string;
  title?: string;
  buttonClassName?: string;
  buttonOffsetRight?: number;
}) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const [chapterNotes, setChapterNotes] = useState<NoteListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function loadNote() {
    setLoading(true);
    Promise.all([
      api.note(pageId),
      chapterId ? api.notes() : Promise.resolve([] as NoteListItem[]),
    ])
      .then(([note, all]) => {
        setContent(note.content || "");
        setSaved(true);
        if (chapterId) {
          setChapterNotes(all.filter((n) => n.chapter_id === chapterId && n.page_id !== pageId));
        }
      })
      .catch(() => setContent(""))
      .finally(() => setLoading(false));
  }

  function openDrawer() {
    setOpen(true);
    loadNote();
  }

  function closeDrawer() {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!saved) doSave(content);
    setOpen(false);
  }

  function handleChange(val: string) {
    setContent(val);
    setSaved(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSave(val), 1200);
  }

  async function doSave(val: string) {
    setSaving(true);
    try {
      await api.saveNote(pageId, val, {
        concept_id: conceptId,
        section_id: sectionId,
        chapter_id: chapterId,
        title,
      });
      setSaved(true);
    } catch {
      // 保存失败不丢数据，下次仍会尝试
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <>
      {/* 触发按钮：右下角固定，书本侧边记东西的感觉 */}
      <button
        onClick={openDrawer}
        className={cn(
          "fixed bottom-[calc(env(safe-area-inset-bottom)+4.75rem)] right-4 z-40 flex items-center gap-1.5 rounded-full border bg-background px-3 py-2 text-sm shadow-md transition-colors hover:text-primary hover:shadow-lg sm:bottom-[4.5rem]",
          buttonClassName
        )}
        style={buttonOffsetRight !== undefined ? { right: buttonOffsetRight } : undefined}
        aria-label="打开笔记"
        title="笔记"
      >
        <NotebookPen className="h-4 w-4" />
        <span className="hidden sm:inline">笔记</span>
      </button>

      {/* 抽屉遮罩 */}
      {open && <div className="fixed inset-0 z-40 bg-black/20" onClick={closeDrawer} />}

      {/* 抽屉 */}
      <div
        className={cn(
          "fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-md flex-col border-l bg-background shadow-2xl transition-transform",
          open ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <NotebookPen className="h-4 w-4 text-primary" />
            笔记
            {title && <span className="text-xs font-normal text-muted-foreground">{title}</span>}
            {saving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
            {saved && !saving && <Save className="h-3 w-3 text-success" />}
          </div>
          <button
            onClick={closeDrawer}
            className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="关闭笔记"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* 当前页笔记 */}
          <div className="mb-5">
            <div className="mb-2 text-xs font-semibold text-muted-foreground">本页笔记</div>
            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : (
              <textarea
                value={content}
                onChange={(e) => handleChange(e.target.value)}
                placeholder="写下你对这个知识点的感悟..."
                rows={8}
                className="w-full resize-none rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            )}
          </div>

          {/* 本章汇总 */}
          {chapterId && (
            <div>
              <div className="mb-2 text-xs font-semibold text-muted-foreground">
                本章其它笔记（{chapterNotes.length}）
              </div>
              {chapterNotes.length === 0 ? (
                <div className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">
                  本章暂无其它笔记
                </div>
              ) : (
                <div className="space-y-2">
                  {chapterNotes.map((n) => (
                    <div key={n.id} className="rounded-md border bg-muted/20 p-2.5">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="truncate text-xs font-medium">
                          {n.concept_title || n.title || n.page_id}
                        </span>
                        {n.concept_id && (
                          <Link
                            href={`/concepts/${n.concept_id}`}
                            onClick={closeDrawer}
                            className="shrink-0 text-[11px] text-primary hover:underline"
                          >
                            跳转 →
                          </Link>
                        )}
                      </div>
                      <p className="line-clamp-3 whitespace-pre-wrap text-xs text-foreground/75">
                        {n.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}